import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  createPdf,
  fetchRecordsOfStore,
} from "../../../redux/Thunk/Order/OrderThunks";
import { useDispatch, useSelector } from "react-redux";
import Loader from "../../Loader";
import Pagination from "../../common/Pagination";
import { setOrderById } from "../../../redux/Reducer/Order/OrderSlice";
import {
  setActiveForm,
} from "../../../redux/Reducer/Admin/PartnerSlice";
import toast from "react-hot-toast";
import { frequentProductList } from "../../../redux/Thunk/Product/ProductThunks";
import { getProductRowTags } from "../../../utils/helpers";
import apiAxios from "../../../services/apiAxios";

const calculateRefillPrediction = (orderDateStr, product) => {
  const rxDays = Number(product?.rxDays) || 0;
  const rxUnits = Number(product?.rxUnits) || 0;
  const orderUnits = Number(product?.orderUnits) || 0;
  const balanceUnits = Number(product?.balanceUnits) || 0;

  const morning = Number(product?.morningTime) || 0;
  const mid = Number(product?.midDay) || 0;
  const evening = Number(product?.eveningTime) || 0;
  const night = Number(product?.night) || 0;
  const extra = Number(product?.extra) || 0;

  let dailyRate = morning + mid + evening + night + extra;
  if (dailyRate <= 0) {
    dailyRate = rxDays > 0 ? (rxUnits / rxDays) : 0;
  }
  if (dailyRate <= 0) {
    dailyRate = 1;
  }

  const daysStockLasts = orderUnits / dailyRate;

  const orderDate = new Date(orderDateStr);
  const finishDate = new Date(orderDate.getTime() + daysStockLasts * 24 * 60 * 60 * 1000);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const finishDateZeroed = new Date(finishDate);
  finishDateZeroed.setHours(0, 0, 0, 0);

  const diffTime = finishDateZeroed.getTime() - today.getTime();
  const daysRemaining = Math.round(diffTime / (1000 * 60 * 60 * 24));

  return {
    daysStockLasts,
    finishDate,
    daysRemaining,
    balanceUnits,
    dailyRate
  };
};

const getOrderRefillStatus = (order) => {
  const predictions = (order?.orderProducts || [])
    .map(p => {
      const pred = calculateRefillPrediction(order.createdAt, p);
      return {
        ...pred,
        product: p
      };
    })
    .filter(pred => pred.balanceUnits > 0);

  if (predictions.length === 0) {
    return {
      status: "Fully Refilled",
      daysRemaining: null,
      finishDate: null,
      classNames: "bg-emerald-50 text-emerald-700 border-emerald-100/80",
      pillClass: "bg-emerald-500",
      description: "All balance units refilled",
      refillAlertSent: false
    };
  }

  predictions.sort((a, b) => a.daysRemaining - b.daysRemaining);
  const earliest = predictions[0];

  const formattedDate = earliest.finishDate.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric"
  });

  let statusObj = {};
  if (earliest.daysRemaining < 0) {
    const overdue = Math.abs(earliest.daysRemaining);
    statusObj = {
      status: "Refill Overdue",
      daysRemaining: earliest.daysRemaining,
      finishDate: earliest.finishDate,
      classNames: "bg-rose-50 text-rose-700 border-rose-100/80 font-bold",
      pillClass: "bg-rose-500",
      description: `Depleted ${overdue} day${overdue > 1 ? "s" : ""} ago (${formattedDate})`
    };
  } else if (earliest.daysRemaining === 0) {
    statusObj = {
      status: "Depleting Today",
      daysRemaining: earliest.daysRemaining,
      finishDate: earliest.finishDate,
      classNames: "bg-amber-50 text-amber-700 border-amber-200/80 font-bold animate-pulse",
      pillClass: "bg-amber-500",
      description: `Stock finishes today (${formattedDate})`
    };
  } else if (earliest.daysRemaining <= 7) {
    statusObj = {
      status: "Refill Due Soon",
      daysRemaining: earliest.daysRemaining,
      finishDate: earliest.finishDate,
      classNames: "bg-amber-50 text-amber-600 border-amber-100 font-bold",
      pillClass: "bg-amber-500",
      description: `Finishes in ${earliest.daysRemaining} day${earliest.daysRemaining > 1 ? "s" : ""} (${formattedDate})`
    };
  } else {
    statusObj = {
      status: "Stock Active",
      daysRemaining: earliest.daysRemaining,
      finishDate: earliest.finishDate,
      classNames: "bg-blue-50 text-blue-700 border-blue-100 font-bold",
      pillClass: "bg-blue-500",
      description: `Finishes in ${earliest.daysRemaining} day${earliest.daysRemaining > 1 ? "s" : ""} (${formattedDate})`
    };
  }

  statusObj.refillAlertSent = !!earliest.product?.refillAlertSent;
  return statusObj;
};

function StoreRecords() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [filteredData, setFilteredData] = useState([]);
  const [orderDetails, setOrderDetails] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const filterDropdownRef = useRef(null);
  const [activeDropdownOID, setActiveDropdownOID] = useState(null);
  const [pendingAttempt, setPendingAttempt] = useState(null);

  const filterOptions = [
    { label: "Refill required in 1 day", value: "1" },
    { label: "Refill required in 2 days", value: "2" },
    { label: "Refill required in 3 days", value: "3" },
    { label: "Refill required in 4 days", value: "4" },
    { label: "Refill required in 5 days", value: "5" },
    { label: "Refill required in 6 days", value: "6" },
    { label: "Refill required in 7 days", value: "7" },
    { label: "Refill required in > 7 days", value: ">7" },
  ];

  const toggleFilter = (val) => {
    setSelectedFilters((prev) =>
      prev.includes(val) ? prev.filter((v) => v !== val) : [...prev, val]
    );
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(e.target)) {
        setShowFilterDropdown(false);
      }
      if (!e.target.closest(".custom-status-dropdown")) {
        setActiveDropdownOID(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const {
    storeRecord: data,
    loading,
    orderById,
  } = useSelector((state) => state?.order);
  const { storeDetail } = useSelector((state) => state?.store);
  const products = useSelector((state) => state?.product?.frequentProduct);

  useEffect(() => {
    const id = storeDetail?.store?.SID;
    if (id) {
      dispatch(fetchRecordsOfStore({ page: 1, limit: 100000, id }));
    }
  }, [dispatch, storeDetail?.store?.SID]);

  useEffect(() => {
    if (orderById && orderById?.OID) {
      setFilteredData([orderById]); // Set it as an array to match the structure of filteredData
    } else if (data?.data?.length > 0) {
      let filteredOrders = data?.data || [];
      setFilteredData(filteredOrders);
    }
  }, [data, orderById]);

  useEffect(() => {
    if (!products?.frequentProducts?.length) {
      const id = storeDetail?.store?.SID;
      let search = "";
      dispatch(frequentProductList({ id, productName: search }));
    }
  }, [dispatch, products?.frequentProducts?.length, storeDetail?.store?.SID]);

  useEffect(() => {
    dispatch(setOrderById({}));
    dispatch(setActiveForm(""));
  }, [dispatch]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); // Reset page on search change
  };

  const searchedOrders = (filteredData || []).filter((order) => {
    let matchesSearch = true;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const patientName = `${order?.patientDetail?.name || ""} ${order?.patientDetail?.surname || ""}`.toLowerCase();
      const patientPhone = (order?.patientDetail?.phoneNumber || "").toLowerCase();
      const oid = (order?.OID || "").toLowerCase();
      const doctorName = `${order?.doctor?.personalInfo?.name || ""} ${order?.doctor?.personalInfo?.surname || ""}`.toLowerCase();
      const clinicName = (order?.doctor?.personalInfo?.clinicName || "").toLowerCase();
      matchesSearch =
        patientName.includes(q) ||
        patientPhone.includes(q) ||
        oid.includes(q) ||
        doctorName.includes(q) ||
        clinicName.includes(q);
    }

    let matchesRefill = true;
    if (selectedFilters.length > 0) {
      const refillStatus = getOrderRefillStatus(order);
      const daysRemaining = refillStatus.daysRemaining;

      matchesRefill = selectedFilters.some((filterVal) => {
        if (filterVal === "1") return daysRemaining !== null && daysRemaining <= 1;
        if (filterVal === "2") return daysRemaining === 2;
        if (filterVal === "3") return daysRemaining === 3;
        if (filterVal === "4") return daysRemaining === 4;
        if (filterVal === "5") return daysRemaining === 5;
        if (filterVal === "6") return daysRemaining === 6;
        if (filterVal === "7") return daysRemaining === 7;
        if (filterVal === ">7") return daysRemaining !== null && daysRemaining > 7;
        return false;
      });
    }

    return matchesSearch && matchesRefill;
  });

  const itemsPerPage = 10;
  const totalPages = Math.ceil((searchedOrders?.length || 0) / itemsPerPage) || 1;

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages, currentPage]);

  const displayedOrders = searchedOrders.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      dispatch(setOrderById({}));
    }
  };

  const handleUpdateRefillStatus = async (oid, status, notes = "") => {
    try {
      const response = await apiAxios.post("/api/order/refill-attempt", {
        OID: oid,
        status,
        notes,
      });
      if (response.status === 200) {
        toast.success("Refill attempt logged successfully!", {
          icon: "📞",
          style: {
            borderRadius: "10px",
            background: "#333",
            color: "#fff",
          },
        });

        // Update local filteredData state reactively
        setFilteredData((prevData) =>
          prevData.map((order) => {
            if (order.OID === oid) {
              const currentAttempts = order.refillAttempts || [];
              const newAttempt = response.data.data;
              return {
                ...order,
                refillAttempts: [newAttempt, ...currentAttempts],
              };
            }
            return order;
          })
        );
      }
    } catch (err) {
      console.error("handleUpdateRefillStatus error:", err);
      toast.error(err.response?.data?.message || "Failed to log refill attempt");
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      dispatch(setOrderById({}));
    }
  };
  const handleViewDetails = (order) => {
    setOrderDetails((prevOrder) => (prevOrder === order ? null : order));
  };
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, "0");
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };
  const handleNewOrder = (order) => {
    dispatch(setOrderById(order));
    dispatch(setActiveForm(""));
    navigate("/store-dashboard", { state: { from: "records" } });
  };
  const handleViewImage = (pathObj) => {
    const path = pathObj?.filePath || pathObj;
    if (path?.length > 0) {
      window.open(path);
    }
  };
  const handleCreatePdf = async (order) => {
    const payload = {
      patient: {
        phoneNumber: order?.patientDetail?.phoneNumber || "",
        name: order?.patientDetail?.name || "",
        surname: order?.patientDetail?.surname || "",
        gender: order?.patientDetail?.gender || "",
        dateofbirth: order?.patientDetail?.dateofbirth || "",
      },
      products: order?.orderProducts?.map((product) => ({
        productId: product?.IID || "",
        productName: product?.productName || "",
        units: Number(product?.units) || 0,
        rxDays: Number(product?.rxDays) || 0,
        orderQty: Number(product?.orderQty) || 0,
        rxUnits: Number(product?.rxUnits) || 0,
        orderUnits: Number(product?.orderUnits) || 0,
        balanceUnits: Number(product?.balanceUnits) || 0,
        dosageNote: product?.dosageNote || "",
        mrp: Number(product?.mrp) || 0,
        discount: Number(product?.discount) || 0,
        gst: Number(product?.gst) || 0,
        netAmt: Number(product?.netAmt) || 0,
        type: product?.type || "",
        typeIndex: "",
        morningTime: Number(product?.morningTime) || 0,
        midDay: Number(product?.midDay) || 0,
        eveningTime: Number(product?.eveningTime) || 0,
        night: Number(product?.night) || 0,
        takenTime: product?.takenTime || "",
        takenTimeIndex: "",
        extra: Number(product?.extra) || 0,
        extraIndex: 0,
        drugs: product?.drugs || [],
        qty: Number(product?.qty) || 0,
        uom: product?.uom || "",
      })),
      delivery: {
        isAddress: order?.isAddress || false,
        isCollect: order?.isCollect || false,
        isClinic: order?.isClinic || false,
        address: {
          premisesNoFloor: order?.premisesNoFloor || "",
          premisesName: order?.premisesName || "",
          landmark: order?.landmark || "",
          areaRoad: order?.areaRoad || "",
          city: order?.city || "",
          pincode: order?.pincode || "",
          state: order?.state || "",
          phoneNumber2: order?.phoneNumber2 || "",
        },
      },
      payment: order?.payment || "",
      DID: order?.DID || "",
    };
    const response = await dispatch(createPdf(payload)).unwrap();
    if (response?.status === 200) {
      if (response?.data?.success && response?.data?.url) {
        const url = response.data.url;
        window.open(url, "_blank");
      }
    } else {
      toast.error(response?.message);
    }
  };

  const copyToClipboard = (text, label) => {
    if (!text) return;
    navigator.clipboard.writeText(text).then(() => {
      toast.success(`${label} copied!`, {
        icon: '📋',
        style: {
          borderRadius: '10px',
          background: '#333',
          color: '#fff',
        },
      });
    });
  };



  return (
    <section className="space-y-4">
      {loading ? <Loader loading={loading} /> : null}

      {/* Search & Filter Row */}
      <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center w-full mb-2 relative z-30">
        {/* Search Input Bar */}
        <div className="relative flex-1">
          <span className="absolute inset-y-0 left-0 flex items-center pl-3.5 pointer-events-none text-slate-400">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="text"
            value={searchQuery}
            onChange={handleSearchChange}
            placeholder="Search by Patient name, Order ID, Doctor, or Phone..."
            className="w-full pl-10 pr-10 py-3 bg-white border border-primary-100 rounded-2xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all shadow-sm"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-600 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>

        {/* Refill Filter Dropdown */}
        <div className="relative w-full sm:w-[260px]" ref={filterDropdownRef}>
          <button
            type="button"
            onClick={() => setShowFilterDropdown(!showFilterDropdown)}
            className="w-full flex items-center justify-between px-4 py-3 bg-white border border-primary-100 rounded-2xl text-sm text-slate-700 hover:border-primary-300 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all shadow-sm"
          >
            <span className="font-semibold truncate">
              {selectedFilters.length > 0
                ? `${selectedFilters.length} Filter${selectedFilters.length > 1 ? "s" : ""} active`
                : "Filter by Refill Days"}
            </span>
            <svg
              className="w-4 h-4 text-slate-400 transition-transform duration-200"
              style={{ transform: showFilterDropdown ? "rotate(180deg)" : "none" }}
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {showFilterDropdown && (
            <div className="absolute z-[100] w-full mt-2 bg-white border border-slate-100 rounded-2xl shadow-xl max-h-[280px] overflow-y-auto p-1.5 space-y-0.5 border-t border-slate-100">
              {filterOptions.map((opt) => (
                <label
                  key={opt.value}
                  className="flex items-center gap-3 px-3 py-2.5 hover:bg-primary-50 rounded-xl cursor-pointer transition-all"
                >
                  <input
                    type="checkbox"
                    checked={selectedFilters.includes(opt.value)}
                    onChange={() => toggleFilter(opt.value)}
                    className="w-4.5 h-4.5 rounded border-slate-200 text-primary-600 focus:ring-primary-500 accent-primary-600 cursor-pointer"
                  />
                  <span className="flex-1 text-[13px] text-slate-700 font-bold">
                    {opt.label}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {displayedOrders?.length > 0 ? (
        displayedOrders.map((order, index) => {
          const hasInvoice = !!(order?.invoice && order?.invoice?.invoiceNo);

          const premisesNoFloor = order?.premisesNoFloor || order?.patientAddress?.premisesNoFloor || order?.deliveryDetail?.premisesNoFloor || "";
          const premisesName = order?.premisesName || order?.patientAddress?.premisesName || order?.deliveryDetail?.premisesName || "";
          const areaRoad = order?.areaRoad || order?.patientAddress?.areaRoad || order?.deliveryDetail?.areaRoad || "";
          const landmark = order?.landmark || order?.patientAddress?.landmark || order?.deliveryDetail?.landmark || "";
          const city = order?.city || order?.patientDetail?.city || order?.patientAddress?.city || order?.deliveryDetail?.city || "";
          const state = order?.state || order?.patientAddress?.state || order?.deliveryDetail?.state || "";
          const pincode = order?.pincode || order?.patientAddress?.pincode || order?.patientDetail?.pincode || order?.deliveryDetail?.pincode || "";

          const addressParts = [
            premisesNoFloor,
            premisesName,
            areaRoad,
            landmark,
            city,
            state,
            pincode
          ].filter(Boolean);

          const hasAddress = addressParts.length > 0;

          let totalBalanceUnits = 0;
          let totalRefillValue = 0;
          order?.orderProducts?.forEach((product) => {
            const balanceUnits = Number(product.balanceUnits) || 0;
            totalBalanceUnits += balanceUnits;
            if (balanceUnits > 0) {
              const packSize = Number(product.units || product.uom || 1) || 1;
              const refillQty = Math.ceil(balanceUnits / packSize);
              const mrp = Number(product.mrp) || 0;
              const discount = Number(product.discount) || 0;
              const refillNetValue = refillQty * mrp * (1 - discount / 100);
              totalRefillValue += refillNetValue;
            }
          });

          const refillStatus = getOrderRefillStatus(order);
          const latestAttempt = order?.refillAttempts?.[0];
          const currentStatus = latestAttempt?.status || "no_attempt";
          const displayedStatus = pendingAttempt?.OID === order.OID ? pendingAttempt.status : currentStatus;

          return (
            <div
              key={order.OID || index}
              className={`bg-white border border-slate-100 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 mb-4 border-l-4 ${totalBalanceUnits === 0
                ? "border-l-emerald-500"
                : refillStatus.status === "Refill Overdue"
                  ? "border-l-rose-500"
                  : refillStatus.status === "Depleting Today" || refillStatus.status === "Refill Due Soon"
                    ? "border-l-amber-500"
                    : "border-l-blue-500"
                }`}
            >
              {/* Main Card Summary */}
              <div
                className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50/30 transition-colors rounded-t-2xl"
                onClick={() => handleViewDetails(order)}
              >
                <div className="grid grid-cols-12 gap-3 items-center">
                  {/* Col 1: Identity, Date & Action Buttons */}
                  <div className="col-span-12 md:col-span-3 md:border-r md:border-slate-100 md:pr-4 space-y-3 flex flex-col justify-center">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Order ID</span>
                      <div className="flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded border border-slate-100" onClick={(e) => e.stopPropagation()}>
                        <span className="font-mono text-sm font-bold text-slate-800">{order?.OID}</span>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(order?.OID, "Order ID");
                          }}
                          className="text-slate-400 hover:text-primary-600 transition-colors p-0.5"
                          title="Copy Order ID"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                      {/* Open DRx */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          order?.pdfPath ? handleViewImage({ filePath: order?.pdfPath }) : handleCreatePdf(order);
                        }}
                        disabled={!!order?.QID}
                        className={`p-2 rounded-xl border text-slate-600 hover:text-primary-700 hover:bg-slate-50 hover:border-primary-200 transition-all ${order?.QID ? "opacity-40 cursor-not-allowed pointer-events-none" : "bg-white border-slate-200"
                          }`}
                        title="Open Digital Prescription (DRx)"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      </button>

                      {/* Open Rx */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewImage({ filePath: order?.filePath });
                        }}
                        disabled={!order?.filePath?.length}
                        className={`p-2 rounded-xl border text-slate-600 hover:text-primary-700 hover:bg-slate-50 hover:border-primary-200 transition-all ${!order?.filePath?.length ? "opacity-40 cursor-not-allowed pointer-events-none" : "bg-white border-slate-200"
                          }`}
                        title="Open Original Prescription (Rx)"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>

                      {/* Create Order */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNewOrder(order);
                        }}
                        className="bg-primary-600 text-white hover:bg-primary-700 transition-all p-2 rounded-xl flex items-center justify-center border border-primary-600"
                        title="Create Order for Balance Units"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>

                      {/* Expand Details Chevron */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetails(order);
                        }}
                        className={`p-2 rounded-xl border border-slate-200 text-slate-500 hover:bg-slate-50 transition-all ${orderDetails === order ? "bg-slate-100 text-slate-800 border-slate-300" : "bg-white"
                          }`}
                        title="Toggle Detailed View"
                      >
                        <svg
                          className={`w-4 h-4 transition-transform duration-300 ${orderDetails === order ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Col 2: Patient & Doctor Info */}
                  <div className="col-span-12 md:col-span-3 md:border-r md:border-slate-100 md:pr-4 space-y-1.5 flex flex-col justify-center">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-slate-900 text-sm">
                        {order?.patientDetail?.name || ""} {order?.patientDetail?.surname || ""}
                      </span>

                      {/* Call Quick Action */}
                      {order?.patientDetail?.phoneNumber && (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <a
                            href={`tel:${order?.patientDetail?.phoneNumber}`}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-blue-50 text-blue-600 hover:bg-blue-100 p-1.5 rounded-full transition-colors"
                            title={`Call ${order?.patientDetail?.phoneNumber}`}
                          >
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M6.62 10.79a15.15 15.15 0 006.59 6.59l2.2-2.2a1 1 0 011.11-.27c1.12.37 2.33.57 3.57.57a1 1 0 011 1V20a1 1 0 01-1 1A17 17 0 013 4a1 1 0 011-1h3.5a1 1 0 011 1c0 1.24.2 2.45.57 3.57a1 1 0 01-.27 1.11l-2.2 2.2z" />
                            </svg>
                          </a>
                        </div>
                      )}
                    </div>

                    <div className="flex items-center gap-1.5 text-xs text-slate-500">
                      <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                      </svg>
                      <span className="truncate max-w-[200px]" title={`Dr. ${order?.doctor?.personalInfo?.name || ""} ${order?.doctor?.personalInfo?.surname || ""}`}>
                        Dr. {order?.doctor?.personalInfo?.name || ""} {order?.doctor?.personalInfo?.surname || ""}
                      </span>
                    </div>

                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      <svg className="w-3.5 h-3.5 text-slate-400 shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span className="shrink-0">{formatDate(order?.createdAt)}</span>
                      <span className={`flex items-center gap-1.5 text-[8px] px-1.5 py-0.5 rounded-full font-bold uppercase tracking-tight shrink-0 border ml-1 ${order?.source === 'quick' ? 'bg-orange-100 text-orange-700 border-orange-200' :
                        order?.source === 'scan' ? 'bg-purple-100 text-purple-700 border-purple-200' :
                        order?.source === 'store' ? 'bg-blue-100 text-blue-700 border-blue-200' :
                        order?.source === 'reorder' ? 'bg-teal-100 text-teal-700 border-teal-200' :
                        'bg-gray-100 text-gray-700 border-gray-200'
                      }`}>
                        {order?.source || 'standard'}
                      </span>
                    </div>
                  </div>

                  {/* Col 3: Refill Balance & Forecast Badge */}
                  <div className="col-span-12 md:col-span-6 space-y-3 flex flex-col justify-center md:pl-2">
                    <div className="flex items-center gap-2 flex-wrap text-left">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Refill Bal</span>
                        <span className={`text-xs font-black px-2 py-0.5 rounded-full ${totalBalanceUnits === 0
                          ? "bg-slate-100 text-slate-600"
                          : "bg-primary-50 text-primary-700 border border-primary-100"
                          }`}>
                          {totalBalanceUnits} unit{totalBalanceUnits !== 1 ? "s" : ""}
                        </span>
                      </div>
                      {totalBalanceUnits > 0 && (
                        <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                          <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border flex items-center gap-1 shrink-0 ${refillStatus.classNames}`}>
                            <span className={`w-1 h-1 rounded-full ${refillStatus.pillClass}`}></span>
                            <span className="uppercase tracking-wider">{refillStatus.status}</span>
                            <span className="opacity-95 font-normal">
                              ({refillStatus.daysRemaining !== null
                                ? (refillStatus.daysRemaining < 0
                                  ? `${Math.abs(refillStatus.daysRemaining)} day${Math.abs(refillStatus.daysRemaining) !== 1 ? "s" : ""} overdue`
                                  : refillStatus.daysRemaining === 0
                                    ? "today"
                                    : `in ${refillStatus.daysRemaining} day${refillStatus.daysRemaining !== 1 ? "s" : ""}`)
                                : ""})
                            </span>
                          </span>
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-100/80 flex items-center gap-0.5 shrink-0" title="Total order value of medicines to be refilled">
                            Refill Value: ₹{totalRefillValue.toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Calling Attempt Status Tracking */}
                    {totalBalanceUnits > 0 && (
                      <div className="mt-1 space-y-1.5 text-left" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5">
                          {/* Custom Status Dropdown */}
                          <div className="relative custom-status-dropdown flex-1">
                            <button
                              type="button"
                              onClick={() => setActiveDropdownOID(activeDropdownOID === order.OID ? null : order.OID)}
                              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-xl border text-[11px] font-bold focus:outline-none transition-all shadow-sm cursor-pointer ${displayedStatus === "no_attempt" ? "bg-slate-50 text-slate-600 border-slate-200 hover:bg-slate-100" :
                                displayedStatus === "no_response" ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100/50" :
                                  displayedStatus === "call_back" ? "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100/50" :
                                    displayedStatus === "agreed" ? "bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100/50" :
                                      displayedStatus === "declined" ? "bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100/50" :
                                        displayedStatus === "ordered" ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200" :
                                          "bg-white text-slate-700 border-slate-200"
                                }`}
                            >
                              <span>
                                {displayedStatus === "no_attempt" ? "📞 No Attempt" :
                                  displayedStatus === "no_response" ? "⏳ No Response" :
                                    displayedStatus === "call_back" ? "🕒 Call Back" :
                                      displayedStatus === "agreed" ? "✅ Agreed" :
                                        displayedStatus === "declined" ? "❌ Declined" :
                                          displayedStatus === "ordered" ? "📦 Ordered" : ""}
                              </span>
                              <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>

                            {activeDropdownOID === order.OID && (
                              <div className="absolute z-[200] left-0 right-0 mt-1 bg-white border border-slate-150 rounded-xl shadow-xl py-1 overflow-hidden">
                                {[
                                  { value: "no_attempt", label: "📞 No Attempt" },
                                  { value: "no_response", label: "⏳ No Response" },
                                  { value: "call_back", label: "🕒 Call Back" },
                                  { value: "agreed", label: "✅ Agreed" },
                                  { value: "declined", label: "❌ Declined" },
                                  { value: "ordered", label: "📦 Ordered" },
                                ].map((opt) => (
                                  <button
                                    key={opt.value}
                                    type="button"
                                    onClick={() => {
                                      setPendingAttempt({
                                        OID: order.OID,
                                        status: opt.value,
                                        notes: ""
                                      });
                                      setActiveDropdownOID(null);
                                    }}
                                    className="w-full text-left px-3 py-2 text-[11px] hover:bg-primary-50 font-bold text-slate-700 flex items-center justify-between transition-colors cursor-pointer"
                                  >
                                    <span>{opt.label}</span>
                                    {displayedStatus === opt.value && (
                                      <span className="text-primary-600 font-bold">✓</span>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={() => {
                              if (pendingAttempt?.OID === order.OID) {
                                setPendingAttempt(null);
                              } else {
                                setPendingAttempt({
                                  OID: order.OID,
                                  status: currentStatus,
                                  notes: latestAttempt?.notes || ""
                                });
                              }
                            }}
                            className={`p-1.5 rounded-lg border transition-colors ${pendingAttempt?.OID === order.OID
                              ? "bg-primary-50 border-primary-300 text-primary-600"
                              : "bg-white border-slate-200 text-slate-400 hover:text-slate-600"
                              }`}
                            title="Add/Edit notes"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>

                        {pendingAttempt?.OID === order.OID ? (
                          <div className="flex items-center gap-1.5 mt-1.5">
                            <input
                              type="text"
                              placeholder="Type call notes..."
                              value={pendingAttempt.notes}
                              onChange={(e) => setPendingAttempt({ ...pendingAttempt, notes: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  const text = pendingAttempt.notes.trim();
                                  if (!text) {
                                    toast.error("Please add a note to save calling status outcome.");
                                    return;
                                  }
                                  handleUpdateRefillStatus(order.OID, pendingAttempt.status, text);
                                  setPendingAttempt(null);
                                } else if (e.key === "Escape") {
                                  setPendingAttempt(null);
                                }
                              }}
                              autoFocus
                              className="flex-1 px-2.5 py-1.5 border border-primary-200 rounded-lg text-[10px] focus:outline-none focus:ring-1 focus:ring-primary-500 shadow-inner bg-slate-50/50"
                            />
                            <button
                              type="button"
                              onClick={() => {
                                const text = pendingAttempt.notes.trim();
                                if (!text) {
                                  toast.error("Please add a note to save calling status outcome.");
                                  return;
                                }
                                handleUpdateRefillStatus(order.OID, pendingAttempt.status, text);
                                setPendingAttempt(null);
                              }}
                              className="px-6 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-[10px] font-bold shadow-sm transition-all"
                            >
                              Save
                            </button>
                            <button
                              type="button"
                              onClick={() => setPendingAttempt(null)}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-[10px] font-bold transition-all"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          latestAttempt?.notes && (
                            <div className="text-[10px] text-slate-500 bg-slate-50/80 px-2 py-1.5 rounded-lg border border-slate-100 flex items-start gap-1.5 mt-1">
                              <span className="flex-1 leading-normal">
                                💬 {latestAttempt.notes}
                              </span>
                              {latestAttempt.new_OID && (
                                <span className="bg-emerald-50 text-emerald-700 px-1 rounded text-[8px] font-bold shrink-0 border border-emerald-100">
                                  OID: {latestAttempt.new_OID}
                                </span>
                              )}
                            </div>
                          )
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Collapsible Details */}
              <div
                className={`grid transition-all duration-300 ease-in-out ${orderDetails === order
                  ? "grid-rows-[1fr] opacity-100 border-t border-slate-100"
                  : "grid-rows-[0fr] opacity-0 overflow-hidden"
                  }`}
              >
                <div className="overflow-hidden bg-slate-50/70 rounded-b-2xl">
                  <div className="p-4 sm:p-5 space-y-4">
                    <div className="grid grid-cols-12 gap-5">

                      {/* Patient Block */}
                      <div className="col-span-12 md:col-span-4 space-y-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <h3 className="font-bold text-slate-800 text-sm">Patient Details</h3>
                        </div>

                        <div className="space-y-2 text-xs">
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Name</span>
                            <span className="col-span-2 font-semibold text-slate-700">{order?.patientDetail?.name || ""} {order?.patientDetail?.surname || ""}</span>
                          </div>
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Contact</span>
                            <span className="col-span-2 font-semibold text-slate-700">{order?.patientDetail?.phoneNumber || "N/A"}</span>
                          </div>
                          {order?.patientDetail?.dateofbirth && (
                            <div className="grid grid-cols-3">
                              <span className="text-slate-400">D.O.B</span>
                              <span className="col-span-2 font-semibold text-slate-700">{formatDate(order?.patientDetail?.dateofbirth)}</span>
                            </div>
                          )}
                          {order?.patientDetail?.gender && (
                            <div className="grid grid-cols-3">
                              <span className="text-slate-400">Gender</span>
                              <span className="col-span-2 font-semibold text-slate-700 capitalize">{order?.patientDetail?.gender}</span>
                            </div>
                          )}
                          {(city || pincode) && (
                            <div className="grid grid-cols-3">
                              <span className="text-slate-400">City/Pincode</span>
                              <span className="col-span-2 font-semibold text-slate-700 capitalize">
                                {[city, pincode].filter(Boolean).join(" - ")}
                              </span>
                            </div>
                          )}
                          {hasAddress && (
                            <div className="grid grid-cols-3">
                              <span className="text-slate-400">Address</span>
                              <span className="col-span-2 font-semibold text-slate-700">
                                {addressParts.join(", ")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Doctor Block */}
                      <div className="col-span-12 md:col-span-4 space-y-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                          <h3 className="font-bold text-slate-800 text-sm">Doctor Details</h3>
                        </div>

                        <div className="space-y-2 text-xs">
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Dr. Name</span>
                            <span className="col-span-2 font-semibold text-slate-700">Dr. {order?.doctor?.personalInfo?.name || ""} {order?.doctor?.personalInfo?.surname || ""}</span>
                          </div>
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Specialty</span>
                            <span className="col-span-2 font-semibold text-slate-700 whitespace-pre-wrap">{order?.doctor?.personalInfo?.qualificationSpecialisation || "General"}</span>
                          </div>
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Contact</span>
                            <span className="col-span-2 font-semibold text-slate-700">{order?.doctor?.contactNumber || "N/A"}</span>
                          </div>
                          <div className="grid grid-cols-3">
                            <span className="text-slate-400">Clinic</span>
                            <span className="col-span-2 font-semibold text-slate-700">{order?.doctor?.personalInfo?.clinicName || "N/A"}</span>
                          </div>
                          {order?.doctor?.clinicAddress && (
                            <div className="grid grid-cols-3">
                              <span className="text-slate-400">Clinic Add.</span>
                              <span className="col-span-2 font-semibold text-slate-700">
                                {[
                                  order?.doctor?.clinicAddress?.city,
                                  order?.doctor?.clinicAddress?.state,
                                  order?.doctor?.clinicAddress?.pinCode,
                                ]
                                  .filter(Boolean)
                                  .join(", ")}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Billing Summary Block */}
                      <div className="col-span-12 md:col-span-4 space-y-3 bg-white p-4 rounded-xl border border-slate-100 shadow-sm flex flex-col justify-between">
                        <div>
                          <div className="flex items-center gap-2 pb-2 border-b border-slate-100 mb-3">
                            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            <h3 className="font-bold text-slate-800 text-sm">Billing Details</h3>
                          </div>

                          <div className="space-y-2 text-xs">
                            {hasInvoice && (
                              <>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Invoice No.</span>
                                  <span className="font-semibold text-slate-700 font-mono">{order?.invoice?.invoiceNo}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-400">Invoice Amt.</span>
                                  <span className="font-semibold text-slate-700">₹{order?.invoice?.invoiceAmount}</span>
                                </div>
                                <div className="border-b border-slate-100 my-2"></div>
                              </>
                            )}
                            <div className="flex justify-between">
                              <span className="text-slate-400">Subtotal</span>
                              <span className="font-medium text-slate-600">₹{order?.billing?.subTotal || 0}</span>
                            </div>
                            {order?.billing?.totalDiscount > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-400">Total Discount</span>
                                <span className="font-semibold text-emerald-600">-₹{order?.billing?.totalDiscount}</span>
                              </div>
                            )}
                            {(() => {
                              const returnAmt = Number(order?.billing?.totalReturnAmount) || 
                                (order?.orderProducts || []).filter(p => p.isReturn).reduce((acc, rp) => {
                                  const mrp = Number(rp.mrp) || 0;
                                  const qty = Number(rp.orderQty) || 0;
                                  const disc = Number(rp.discount) || 0;
                                  const unit = disc > 0 ? (mrp - (mrp * disc) / 100) : mrp;
                                  return acc + (unit * qty);
                                }, 0);
                              return returnAmt > 0 ? (
                                <div className="flex justify-between text-rose-700 font-semibold">
                                  <span>Returned Products</span>
                                  <span>-₹{parseFloat(returnAmt).toFixed(2)}</span>
                                </div>
                              ) : null;
                            })()}
                            {order?.billing?.handlingFee > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-400">Handling Fee</span>
                                <span className="font-medium text-slate-600">₹{order?.billing?.handlingFee}</span>
                              </div>
                            )}
                            {order?.billing?.deliveryCharges > 0 && (
                              <div className="flex justify-between">
                                <span className="text-slate-400">Delivery Charges</span>
                                <span className="font-medium text-slate-600">₹{order?.billing?.deliveryCharges}</span>
                              </div>
                            )}
                            <div className="flex justify-between border-t border-slate-100 pt-2 font-bold text-sm">
                              <span className="text-slate-800">Net Amount</span>
                              <span className="text-primary-700">₹{order?.billing?.payableAmount || 0}</span>
                            </div>
                          </div>
                        </div>

                        <span className="text-[10px] text-slate-400 leading-tight italic block mt-3">
                          *Estimated amount. Confirmed at 'Packed' stage.
                        </span>
                      </div>

                      {/* Revamped Medicine List Block */}
                      <div className="col-span-12 bg-white p-4 rounded-xl border border-slate-100 shadow-sm space-y-3">
                        <div className="flex items-center gap-2 pb-2 border-b border-slate-100">
                          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                          </svg>
                          <h3 className="font-bold text-slate-800 text-sm">Prescribed Medicines</h3>
                        </div>

                        <div className="space-y-2">
                          {[...(order?.orderProducts || [])]
                            .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0))
                            .map((product, idx) => {
                              const pred = calculateRefillPrediction(order.createdAt, product);

                              let predLabel = "";
                              let predClass = "";
                              if (pred.balanceUnits <= 0) {
                                predLabel = "Fully Refilled";
                                predClass = "bg-emerald-50 text-emerald-700 border-emerald-100/50 font-bold";
                              } else if (pred.daysRemaining < 0) {
                                const overdueDays = Math.abs(pred.daysRemaining);
                                predLabel = `Finished ${overdueDays} day${overdueDays !== 1 ? "s" : ""} ago`;
                                predClass = "bg-rose-50 text-rose-700 border-rose-100/80 font-bold";
                              } else if (pred.daysRemaining === 0) {
                                predLabel = `Finishes Today`;
                                predClass = "bg-amber-50 text-amber-700 border-amber-200/80 font-bold animate-pulse";
                              } else if (pred.daysRemaining <= 7) {
                                predLabel = `Finishes in ${pred.daysRemaining} day${pred.daysRemaining !== 1 ? "s" : ""}`;
                                predClass = "bg-amber-50 text-amber-600 border-amber-100 font-bold";
                              } else {
                                predLabel = `Finishes in ${pred.daysRemaining} day${pred.daysRemaining !== 1 ? "s" : ""}`;
                                predClass = "bg-blue-50 text-blue-700 border-blue-100 font-bold";
                              }

                              let dosageStr = "";
                              if (product?.isSOS || product?.dosageMode === "SOS") {
                                dosageStr = "SOS (As Needed)";
                              } else if (product?.isNA || product?.dosageMode === "NA") {
                                dosageStr = "NA";
                              } else if (product?.isPeriodic || product?.dosageMode === "PERIODIC") {
                                const freq = product?.periodicFrequency || "Periodic";
                                dosageStr = freq;
                              } else if (product?.isReducingDose || product?.dosageMode === "REDUCING") {
                                const steps = Array.isArray(product?.reducingDoses) ? product?.reducingDoses : [];
                                if (steps.length > 0) {
                                  dosageStr = "Reducing: " + steps.map(s => `Days ${s.startDay}-${s.endDay}: ${s.morningTime||0}-${s.midDay||0}-${s.eveningTime||0}-${s.night||0}`).join("; ");
                                } else {
                                  dosageStr = "Reducing Dose";
                                }
                              } else {
                                const morning = Number(product?.morningTime) || 0;
                                const mid = Number(product?.midDay) || 0;
                                const evening = Number(product?.eveningTime) || 0;
                                const night = Number(product?.night) || 0;
                                dosageStr = `${morning}-${mid}-${evening}-${night}`;
                              }

                              const packSize = Number(product?.units || product?.uom || 1) || 1;
                              const balanceUnits = Number(product?.balanceUnits) || 0;
                              const balancePacks = Math.ceil(balanceUnits / packSize);

                              const mrp = Number(product?.mrp) || 0;
                              const orderQty = Number(product?.orderQty) || 0;
                              const totalValue = orderQty * mrp;
                              const discount = Number(product?.discount) || 0;
                              const netValue = Number(product?.netAmt !== undefined && product?.netAmt !== null ? product?.netAmt : (orderQty * mrp * (1 - discount / 100)));
                              const refillQty = Math.ceil(balanceUnits / packSize);
                              const refillNetValue = refillQty * mrp * (1 - discount / 100);

                              return (
                                <div key={product.IID || idx} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-all gap-2 text-xs">
                                  <div className="space-y-1.5 flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className="font-bold text-slate-800 text-sm truncate max-w-[280px]" title={product?.productName}>{product?.productName || "Unknown Medicine"}</span>
                                      <span className="bg-slate-100 text-slate-600 px-2 py-0.5 rounded text-[10px] uppercase font-semibold">{product?.type || "Tab"}</span>
                                      {(() => {
                                        const tags = getProductRowTags(product);
                                        return tags.length > 0 ? (
                                          <div className="flex items-center gap-1 flex-wrap">
                                            {tags.map((tag, tIdx) => {
                                              const isNRx = tag.name?.toUpperCase()?.includes("NRX");
                                              return (
                                                <span
                                                  key={tag.id || `${tag.name}-${tIdx}`}
                                                  className={`inline-flex items-center px-1.5 py-0.5 rounded-[4px] text-[10px] font-bold tracking-tight border ${
                                                    isNRx
                                                      ? "bg-red-50 text-red-700 border-red-300"
                                                      : "border-slate-200"
                                                  }`}
                                                  style={
                                                    !isNRx
                                                      ? {
                                                          backgroundColor: `${tag.color || "#3B82F6"}18`,
                                                          color: tag.color || "#1D4ED8",
                                                          borderColor: `${tag.color || "#3B82F6"}40`,
                                                        }
                                                      : {}
                                                  }
                                                  title={tag.source ? `${tag.name} (${tag.source === "drug" ? "Drug Tag" : "Product Tag"})` : tag.name}
                                                >
                                                  {tag.name}
                                                </span>
                                              );
                                            })}
                                          </div>
                                        ) : null;
                                      })()}
                                      {predLabel && (
                                        <span className={`px-2 py-0.5 rounded-full border text-[10px] flex items-center gap-1 shrink-0 ${predClass}`}>
                                          {predLabel}
                                          {product?.refillAlertSent && (
                                            <span className="text-emerald-600 hover:text-emerald-700 transition-colors animate-fade-in" title="WhatsApp refill alert sent successfully for this product">
                                              <svg className="w-3.5 h-3.5 shrink-0" fill="currentColor" viewBox="0 0 24 24">
                                                <path d="M12.012 2c-5.506 0-9.989 4.478-9.99 9.984a9.96 9.96 0 001.33 4.982L2 22l5.164-1.354a9.959 9.959 0 004.845 1.254h.005c5.507 0 9.991-4.479 9.992-9.986.002-2.668-1.037-5.176-2.927-7.067C17.192 3.056 14.682 2 12.012 2zm5.72 14.126c-.313.882-1.82 1.628-2.5 1.706-.615.07-1.42.124-2.274-.15-.853-.274-1.898-.683-3.238-1.226-5.717-2.316-9.429-8.113-9.715-8.497-.285-.384-2.31-3.08-2.31-5.877 0-2.797 1.455-4.168 1.975-4.708.52-.54 1.135-.675 1.515-.675.38 0 .76.015 1.096.03.36.015.84-.14 1.314.99.488 1.185 1.666 4.07 1.811 4.369.146.3.245.645.045 1.045-.2.4-.3.65-.6.99-.3.34-.63.76-.9 1.02-.27.26-.55.54-.24 1.07.31.53 1.383 2.274 2.969 3.687 2.036 1.815 3.754 2.378 4.28 2.603.527.225.836.195 1.15-.165.313-.36 1.354-1.575 1.716-2.115.362-.54.726-.45 1.224-.265.5.185 3.167 1.493 3.66 1.738.494.245.824.36 1.03.71.206.35.206 2.02-.107 2.902z" />
                                              </svg>
                                            </span>
                                          )}
                                        </span>
                                      )}
                                    </div>

                                    <div className="flex items-center gap-3 text-slate-500 flex-wrap text-[11px]">
                                      <span>Dosage: <strong className="text-slate-700">{dosageStr}</strong> ({product?.takenTime || "A/F"})</span>
                                      <span className="text-slate-300">|</span>
                                      <span>Duration: <strong className="text-slate-700">{product?.rxDays || 0} Days</strong></span>
                                    </div>

                                    {/* Pricing Breakdown Inline */}
                                    <div className="flex items-center gap-2 text-slate-400 text-[11px] flex-wrap bg-slate-50/50 px-2 py-1 rounded border border-slate-100/50 w-fit">
                                      <span>Pack MRP: <strong className="text-slate-700">₹{mrp.toFixed(2)}</strong></span>
                                      <span className="text-slate-300">|</span>
                                      <span>Total: <strong className="text-slate-700">₹{totalValue.toFixed(2)}</strong></span>
                                      {discount > 0 && (
                                        <>
                                          <span className="text-slate-300">|</span>
                                          <span>Discount: <strong className="text-emerald-600">{discount}% Off</strong></span>
                                        </>
                                      )}
                                      <span className="text-slate-300">|</span>
                                      <span>Net: <strong className="text-primary-700 font-bold">₹{netValue.toFixed(2)}</strong></span>
                                      {balanceUnits > 0 && (
                                        <>
                                          <span className="text-slate-300">|</span>
                                          <span>Refill Value: <strong className="text-emerald-700 font-bold">₹{refillNetValue.toFixed(2)}</strong></span>
                                        </>
                                      )}
                                    </div>
                                  </div>

                                  {/* Right Side: Refill Balance and ordered/prescribed stats */}
                                  <div className="flex flex-col items-start sm:items-end justify-center text-slate-500 shrink-0 gap-1 pl-2 border-l-0 sm:border-l sm:border-slate-100 min-w-[200px]">
                                    <div className="text-[11px]">Prescribed: <strong className="text-slate-700">{product?.rxUnits || 0} U</strong></div>
                                    <div className="text-[11px]">Ordered: <strong className="text-slate-700">{product?.orderUnits || 0} U ({orderQty} pack{orderQty !== 1 ? 's' : ''})</strong></div>
                                    <div className="bg-primary-50 px-2 py-1 rounded border border-primary-100 text-primary-700 font-bold text-[11px] mt-0.5">
                                      Refill Bal.: {balanceUnits} U ({balancePacks} pack{balancePacks !== 1 ? 's' : ''})
                                    </div>
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      </div>

                      {/* Calling Attempts Timeline Block */}
                      {order?.refillAttempts?.length > 0 && (
                        <div className="col-span-12 bg-white p-5 rounded-xl border border-slate-100 shadow-sm space-y-4">
                          <div className="flex items-center justify-between pb-2 border-b border-slate-100">
                            <div className="flex items-center gap-2">
                              <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <h3 className="font-bold text-slate-800 text-sm">Refill Calling Timeline</h3>
                            </div>
                            <span className="text-[10px] text-slate-400 uppercase font-semibold">
                              {order?.refillAttempts?.length || 0} Attempt{order?.refillAttempts?.length !== 1 ? 's' : ''} Logged
                            </span>
                          </div>

                          <div className="relative border-l border-slate-200 ml-3.5 pl-6 space-y-5 my-2">
                            {order.refillAttempts.map((attempt, idx) => {
                              const dateStr = new Date(attempt.createdAt).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true
                              });

                              let badgeColor = "bg-slate-50 text-slate-600 border-slate-200";
                              let statusText = "No Attempt";

                              if (attempt.status === "no_response") {
                                badgeColor = "bg-amber-50 text-amber-700 border-amber-200";
                                statusText = "⏳ No Response";
                              } else if (attempt.status === "call_back") {
                                badgeColor = "bg-blue-50 text-blue-700 border-blue-200";
                                statusText = "🕒 Call Back";
                              } else if (attempt.status === "agreed") {
                                badgeColor = "bg-emerald-50 text-emerald-700 border-emerald-200";
                                statusText = "✅ Agreed";
                              } else if (attempt.status === "declined") {
                                badgeColor = "bg-rose-50 text-rose-700 border-rose-200";
                                statusText = "❌ Declined";
                              } else if (attempt.status === "ordered") {
                                badgeColor = "bg-emerald-100 text-emerald-800 border-emerald-300";
                                statusText = "📦 Ordered";
                              }

                              return (
                                <div key={attempt.id || idx} className="relative group">
                                  {/* Timeline Dot */}
                                  <span className={`absolute -left-[31px] top-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border bg-white ring-4 ring-white ${attempt.status === "ordered" ? "border-emerald-500 bg-emerald-500 animate-pulse" :
                                    attempt.status === "declined" ? "border-rose-500 bg-rose-50" :
                                      attempt.status === "agreed" ? "border-emerald-500 bg-emerald-50" :
                                        attempt.status === "call_back" ? "border-blue-500 bg-blue-50" :
                                          "border-slate-300 bg-slate-50"
                                    }`}>
                                    {attempt.status === "ordered" && <span className="h-1.5 w-1.5 rounded-full bg-white"></span>}
                                  </span>

                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5 text-left">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <span className={`px-2.5 py-0.5 rounded-full border text-[10px] font-bold ${badgeColor}`}>
                                        {statusText}
                                      </span>
                                      <span className="text-[11px] text-slate-400 font-medium">
                                        {dateStr}
                                      </span>
                                    </div>
                                  </div>

                                  {attempt.notes && (
                                    <div className="mt-1.5 text-xs text-slate-600 bg-slate-50/50 p-2.5 rounded-xl border border-slate-100 leading-normal max-w-2xl text-left">
                                      {attempt.notes}
                                    </div>
                                  )}
                                  {attempt.new_OID && (
                                    <div className="mt-1.5 flex items-center gap-1.5 text-left">
                                      <span className="text-[9px] font-bold uppercase tracking-wider text-slate-400">Order Placed:</span>
                                      <span className="bg-emerald-50 border border-emerald-100 text-emerald-800 text-[10px] font-bold px-2.5 py-0.5 rounded-md">
                                        OID: {attempt.new_OID}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      ) : (
        <div className="bg-white border border-slate-100 rounded-2xl p-8 text-center text-slate-500 shadow-sm">
          <svg className="w-12 h-12 text-slate-300 mx-auto mb-3" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 13.5h3.86a2.25 2.25 0 012.008 1.24l.885 1.77a2.25 2.25 0 002.007 1.24h1.98a2.25 2.25 0 002.007-1.24l.885-1.77a2.25 2.25 0 012.007-1.24h3.86m-18 0h18" />
          </svg>
          <p className="font-semibold text-slate-600 text-sm">No record matches your search</p>
          <p className="text-xs text-slate-400 mt-1">Try refining your search terms or verify the page count</p>
        </div>
      )}

      {searchedOrders?.length > 0 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPreviousPage={handlePreviousPage}
          onNextPage={handleNextPage}
          isPreviousDisabled={currentPage === 1}
          isNextDisabled={currentPage === totalPages}
          itemLength={displayedOrders?.length}
          totalItems={searchedOrders?.length}
        />
      )}
    </section>
  );
}

export default StoreRecords;
